import { CheckCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { formatDateTime } from '@/utils/format'
import { useGrade } from '../api'
import type { Submission } from '../types'
import { FileChips } from './FileChips'
import { RichText } from './RichText'
import { useFieldErrors } from './useFieldErrors'

/** Read a student's work and give points (within the maximum) and/or feedback. */
export function GradeDialog({
  studentName,
  submission,
  maxPoints,
  onOpenChange,
}: {
  studentName: string
  submission: Submission
  maxPoints: number | null
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('lms')
  const withGuardians = useGuardians()
  const grade = useGrade()
  const [points, setPoints] = useState(submission.points !== null ? String(submission.points) : '')
  const [feedback, setFeedback] = useState(submission.feedback ?? '')
  const { errors, formError, reset, apply } = useFieldErrors(['points', 'feedback'] as const)

  const submit = async () => {
    reset()
    try {
      await grade.mutateAsync({
        id: submission.id,
        points: maxPoints !== null && points !== '' ? Number(points) : null,
        feedback: feedback.trim() || null,
      })
      toast.success(t('Grade saved'), studentName)
      onOpenChange(false)
    } catch (e) {
      apply(e)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<CheckCheck />}
      title={t('Work of {{name}}', { name: studentName })}
      description={t('Handed in {{date}}', { date: formatDateTime(submission.submittedAt) })}
      dismissible={!grade.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={grade.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={grade.isPending}>
            {submission.gradedAt ? t('Update grade') : t('Save grade')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {submission.late ? <Badge tone="warning">{t('Handed in late')}</Badge> : null}
        <div className="rounded-control border border-line bg-surface-muted/40 p-4">
          {submission.body ? (
            <RichText text={submission.body} />
          ) : (
            <p className="text-sm text-muted">{t('No text, see the files.')}</p>
          )}
          {submission.files.length ? (
            <div className="mt-3">
              <FileChips files={submission.files} />
            </div>
          ) : null}
        </div>
        {formError ? (
          <Alert tone="danger" title={t('The grade was not saved')}>
            {formError}
          </Alert>
        ) : null}
        {maxPoints !== null ? (
          <FormField
            label={t('Points (out of {{max}})', { max: maxPoints })}
            error={errors.points}
            className="sm:max-w-48"
          >
            <Input
              type="number"
              min={0}
              max={maxPoints}
              step={0.5}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
          </FormField>
        ) : null}
        <FormField
          label={t('Feedback')}
          error={errors.feedback}
          hint={withGuardians ? t('The student and their guardians see it.') : t('The student sees it.')}
        >
          <Textarea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        </FormField>
      </div>
    </Dialog>
  )
}
